import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'

import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import {
  ErrorResponse,
  InitConfirmCreateTerminationRequest,
  InitConfirmOperationResponse,
  initConfirmOperationResponseSchema,
} from '../../../common/validations'
import { ResidentAppError } from '../../../common/error'
import { ResidentAppErrorCode } from '../../../common/types'
import { getContractQueryKey } from './useAPIGetContract'
import { getContractsQueryKey } from './useAPIGetContracts'

import { post } from 'api/apiClient'
import { rethrowIdentificationRequired } from 'utils/rethrowIdentificationRequired'

const PASS_THROUGH_ERROR_CODES: `${ResidentAppErrorCode}`[] = [
  ResidentAppErrorCode.STALE_CLIENT_REFRESH_REQUIRED,
  ResidentAppErrorCode.TERMINATE_CONTRACT_MISSING_RESIDENT_CREDENTIALS,
  ResidentAppErrorCode.TERMINATE_CONTRACT_PENDING_REQUEST,
]

export const useAPIConfirmCreateTermination = (
  contractUUID: string,
  locale: string,
  options?: UseMutationOptions<
    InitConfirmOperationResponse,
    ErrorResponse,
    Omit<InitConfirmCreateTerminationRequest, 'locale'>
  >
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...mutationOptions } = options ?? {}

  const { mutate, isSuccess, isError, error, data, isPending } = useMutation({
    mutationFn: ({ operationData, provider }) =>
      post(
        `${API_ENDPOINTS.CONTRACTS}/${contractUUID}/confirm-operation/init/contract-termination/create`,
        {
          operationData,
          locale,
          provider,
        },
        initConfirmOperationResponseSchema
      ),
    ...mutationOptions,
    onSuccess: async (response, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getContractsQueryKey(),
        exact: true,
      })
      await queryClient.invalidateQueries({
        queryKey: getContractQueryKey(contractUUID),
      })

      if (onSuccess) {
        await onSuccess(response, variables, onMutateResult, context)
      }
    },
  })

  if (isError) {
    if (
      error instanceof ResidentAppError &&
      PASS_THROUGH_ERROR_CODES.includes(error.errorCode)
    ) {
      throw error
    }

    rethrowIdentificationRequired(error)

    throw ResidentAppError.ClientError('TERMINATE_CONTRACT_INIT_CONFIRM_ERROR')
  }

  return {
    mutate,
    isSuccess,
    data,
    isPending,
  }
}
